import { Lightning } from "@lightningjs/sdk";
function clamp(value, min, max) {
    return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
}
// Match varying precision in both stages, including WebGL 1 implementations
// without highp fragment support. lowp is insufficient for backdrop UVs.
const precision = `
  #ifdef GL_FRAGMENT_PRECISION_HIGH
  precision highp float;
  #else
  precision mediump float;
  #endif
`;
/**
 * Refracts an uncropped render-to-texture backdrop. Apply to a separate, white
 * element behind the foreground content, never to the backdrop itself.
 * See docs/liquid-glass.md for the composition and coordinate contract.
 */
export class LiquidGlassShader extends Lightning.shaders.WebGLDefaultShader {
    constructor() {
        super(...arguments);
        this._backdropSize = new Float32Array([1, 1]);
        this._backdropOffset = new Float32Array([0, 0]);
        this._options = {
            radius: 16,
            bezelWidth: 20,
            thickness: 25,
            refraction: 0.8,
            blur: 14,
            dispersion: 0.5,
            lightAngle: -45,
            lightIntensity: 0.8,
            rimWidth: 1.3,
            rimOpacity: 0.2,
        };
    }
    set backdropSize(size) {
        this._backdropSize = new Float32Array(size.map((n) => Math.max(1, n)));
        this.redraw();
    }
    set backdropOffset(offset) {
        this._backdropOffset = new Float32Array(offset);
        this.redraw();
    }
    set options(options) {
        this._options = Object.assign(Object.assign({}, this._options), options);
        this.redraw();
    }
    setupUniforms(operation) {
        super.setupUniforms(operation);
        const w = Math.max(1, operation.getElementWidth(0));
        const h = Math.max(1, operation.getElementHeight(0));
        const halfSize = Math.min(w, h) / 2;
        const options = this._options;
        const angle = (options.lightAngle * Math.PI) / 180;
        const { gl } = this;
        this._setUniform("size", new Float32Array([w, h]), gl.uniform2fv);
        this._setUniform("backdropSize", this._backdropSize, gl.uniform2fv);
        this._setUniform("backdropOffset", this._backdropOffset, gl.uniform2fv);
        this._setUniform("shape", new Float32Array([
            clamp(options.radius, 0, halfSize),
            clamp(options.bezelWidth, 0.001, halfSize),
            clamp(options.thickness, 0, halfSize),
            clamp(options.refraction, 0, 1),
        ]), gl.uniform4fv);
        this._setUniform("surface", new Float32Array([
            clamp(options.blur, 0, 32),
            clamp(options.dispersion, 0, 1),
            clamp(options.rimWidth, 0, halfSize),
            clamp(options.rimOpacity, 0, 1),
        ]), gl.uniform4fv);
        this._setUniform("lightDirection", new Float32Array([Math.cos(angle), Math.sin(angle)]), gl.uniform2fv);
        this._setUniform("lightIntensity", clamp(options.lightIntensity, 0, 1), gl.uniform1f);
        this._setUniform("pixelSize", 1 / this.ctx.stage.getRenderPrecision(), gl.uniform1f);
    }
}
LiquidGlassShader.vertexShaderSource = `
    ${precision}
    attribute vec2 aVertexPosition;
    attribute vec2 aTextureCoord;
    attribute vec4 aColor;
    uniform vec2 projection;
    varying vec2 vTextureCoord;
    varying vec4 vColor;

    void main() {
      vec2 clip = aVertexPosition * vec2(projection.x, -abs(projection.y)) + vec2(-1.0, 1.0);
      gl_Position = vec4(clip.x, clip.y * -sign(projection.y), 0.0, 1.0);
      vTextureCoord = aTextureCoord;
      vColor = aColor;
    }
  `;
LiquidGlassShader.fragmentShaderSource = `
    ${precision}
    varying vec2 vTextureCoord;
    varying vec4 vColor;
    uniform sampler2D uSampler;
    uniform vec2 size;
    uniform vec2 backdropSize;
    uniform vec2 backdropOffset;
    // radius, bezel width, thickness, refraction
    uniform vec4 shape;
    // blur radius, dispersion, rim width, rim opacity
    uniform vec4 surface;
    uniform vec2 lightDirection;
    uniform float lightIntensity;
    uniform float pixelSize;

    vec4 sampleBackdrop(vec2 position) {
      vec2 inset = vec2(0.5 * pixelSize) / backdropSize;
      vec2 uv = clamp(position / backdropSize, inset, vec2(1.0) - inset);
      return texture2D(uSampler, uv);
    }

    // A fixed spiral avoids the repeated edges of a sparse grid kernel.
    // Its angles, radii and weights are constant for the compiler to fold.
    vec4 blurredBackdrop(vec2 position) {
      if (surface.x <= 0.001) return sampleBackdrop(position);
      vec4 colour = vec4(0.0);
      float total = 0.0;
      for (int i = 0; i < 24; i++) {
        float radiusSquared = (float(i) + 0.5) / 24.0;
        float angle = float(i) * 2.39996323;
        vec2 offset = vec2(cos(angle), sin(angle)) * sqrt(radiusSquared);
        float weight = exp(-2.0 * radiusSquared);
        colour += sampleBackdrop(position + offset * surface.x) * weight;
        total += weight;
      }
      return colour / total;
    }

    void main() {
      vec2 local = vTextureCoord * size;
      vec2 p = local - size * 0.5;
      vec2 q = abs(p) - size * 0.5 + shape.x;
      vec2 corner = max(q, 0.0);
      float cornerLength = length(corner);
      float distance = min(max(q.x, q.y), 0.0) + cornerLength - shape.x;
      float coverage = 1.0 - smoothstep(-pixelSize, 0.0, distance);
      if (coverage <= 0.0) {
        gl_FragColor = vec4(0.0);
        return;
      }

      // The rounded-box distance gradient gives the outward surface normal.
      vec2 normal = cornerLength > 0.001
        ? sign(p) * corner / max(cornerLength, 0.001)
        : (q.x > q.y ? vec2(sign(p.x), 0.0) : vec2(0.0, sign(p.y)));
      float edge = 1.0 - clamp(-distance / shape.y, 0.0, 1.0);
      float slope = edge * edge * 0.95;
      vec3 surfaceNormal = vec3(normal * slope, sqrt(1.0 - slope * slope));

      // Snell's law: trace the refracted ray through the curved glass bezel
      // to the backdrop plane. The flat centre has no displacement.
      float indexOfRefraction = 1.0 + shape.w * 0.5;
      vec3 ray = refract(vec3(0.0, 0.0, -1.0), surfaceNormal, 1.0 / indexOfRefraction);
      vec2 displacement = ray.xy / max(-ray.z, 0.001) * shape.z;
      vec2 position = backdropOffset + local + displacement;
      vec4 colour = blurredBackdrop(position);

      // Subtle wavelength separation at the curved edges (two extra taps).
      vec2 split = displacement * surface.y * 0.08;
      vec4 red = sampleBackdrop(position + split);
      vec4 blue = sampleBackdrop(position - split);
      colour.r += (red.r - blue.r) * 0.25;
      colour.b += (blue.b - red.b) * 0.25;

      float facing = dot(normal, lightDirection);
      float highlight = pow(max(facing, 0.0), 4.0);
      float reflection = highlight + 0.35 * pow(max(-facing, 0.0), 4.0);
      float rim = 1.0 - smoothstep(surface.z, surface.z + pixelSize, -distance);
      float rimLight = rim * surface.w * (0.2 + 0.8 * abs(facing));
      float bevelLight = reflection * edge * edge * lightIntensity * 0.09;
      colour.rgb = mix(colour.rgb, vec3(colour.a), clamp(rimLight + bevelLight, 0.0, 1.0));
      // Lightning uses premultiplied alpha, including the element's fade.
      colour.rgb = clamp(colour.rgb, vec3(0.0), vec3(colour.a));
      gl_FragColor = colour * coverage * vColor;
    }
  `;
//# sourceMappingURL=LiquidGlassShader.js.map